import Grid from '@mui/material/Grid';
import { useAtom } from 'jotai';
import { useState } from 'react';

import CloudSourceExportDialog from '@/features/CloudSourceExportDialog';
import type { CloudSourceTableHeaderProps } from '@/features/DevicesTable/DevicesTable.types';
import SearchBar from '@/features/SearchBar';
import { useSourceDialog } from '@/features/SourceCreate/SourceCreate.hooks';
import { useFetchSourceTypeBasic } from '@/features/SourceCreate/SourceCreate.service';
import { SourceDialogMode } from '@/features/SourceCreate/SourceCreate.types';
import useFetchEnvironment, {
  useCrudGuard
} from '@/pages/Administration/Administration.hooks';
import { useStyles } from '@/pages/Administration/Devices';
import { limaSourcesFiltersAtom } from '@/pages/Administration/Devices/Devices.store';
import theme from '@/theme';

import { cloudSourcesExportAtom } from '../CloudSourcesTable.store';
import { BulkActionsToolbar, DefaultActionsToolbar } from '../components';
import { useCloudSourceActivation } from '../hooks/useCloudSourceActivation.hook';
import { useLimaQuickFilters } from '../hooks/useLimaQuickFilters.hook';

const LimaCloudSourcesTableHeader = (props: CloudSourceTableHeaderProps) => {
  const {
    collapsed = false,
    refreshSources,
    gridRef,
    isCustomerCloudSourcePage = false
  } = props;
  const { classes } = useStyles({
    deviceDetailsCollapsed: collapsed
  });

  const [limaSourcesFilters, setLimaSourcesFilters] = useAtom(
    limaSourcesFiltersAtom
  );
  const [exportData] = useAtom(cloudSourcesExportAtom);
  const [cloudSourceExportDialogOpen, setCloudSourceExportDialogOpen] =
    useState(false);

  // Use extracted hooks
  const activation = useCloudSourceActivation();
  const { limaSourcesQuickFilters } = useLimaQuickFilters();

  useFetchEnvironment();
  useFetchSourceTypeBasic(isCustomerCloudSourcePage);
  const { openSourceDialog } = useSourceDialog();

  const addLimaSourceHandler = useCrudGuard(() => {
    openSourceDialog(SourceDialogMode.New);
  });

  return (
    <>
      <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
        <Grid sx={{ display: 'flex' }}>
          <SearchBar
            className="px-8 pb-4"
            searchbarInputContainerStyle={{
              [theme.breakpoints.down('xxl')]: {
                width: '30%'
              },
              [theme.breakpoints.down('xl')]: {
                width: '45%'
              },
              [theme.breakpoints.down('lg')]: {
                width: '70%'
              },
              display: 'flex',
              minWidth: '22vw'
            }}
            searchableSelectFilters={limaSourcesQuickFilters}
            selectedFilters={limaSourcesFilters}
            onFilterChange={setLimaSourcesFilters}
          />
        </Grid>

        {activation.selectedActivationCount > 0 ? (
          <BulkActionsToolbar
            count={activation.selectedActivationCount}
            actionLabel={activation.actionLabel}
            onActivateDeactivate={() =>
              activation.handleActivateDeactivate(gridRef, refreshSources)
            }
            onCancelSelection={activation.handleCancelSelection}
            gridRef={gridRef}
          />
        ) : (
          <DefaultActionsToolbar
            onAdd={addLimaSourceHandler}
            onExport={() => setCloudSourceExportDialogOpen(true)}
            onRefresh={refreshSources}
            collapsed={collapsed}
            exportData={exportData}
            addButtonClassName={classes.floatInlineEnd}
          />
        )}
      </Grid>
      <activation.ConfirmActivateDeactivateModal />
      <CloudSourceExportDialog
        handleClose={() => setCloudSourceExportDialogOpen(false)}
        open={cloudSourceExportDialogOpen}
      />
    </>
  );
};
export default LimaCloudSourcesTableHeader;
